(() => {
  if (customElements.get('csb-product-tour')) return;

  const sceneScripts = new Map();
  const loadSceneScript = (url) => {
    if (customElements.get('csb-product-scene')) return Promise.resolve();
    if (sceneScripts.has(url)) return sceneScripts.get(url);
    const script = document.createElement('script');
    const pending = new Promise((resolve, reject) => {
      script.src = url;
      script.async = true;
      script.onload = () => customElements.get('csb-product-scene')
        ? resolve() : reject(new Error('Product scene did not register'));
      script.onerror = () => reject(new Error('Product scene could not load'));
      document.head.appendChild(script);
    }).catch((error) => {
      script.remove();
      sceneScripts.delete(url);
      throw error;
    });
    sceneScripts.set(url, pending);
    return pending;
  };

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const ease = (value) => 1 - Math.pow(1 - value, 3);
  const stops = Array.from({ length: 9 }, (_, index) => index / 8);
  const keys = ['overview', 'steel', 'words', 'cards', 'pen', 'engrave', 'close', 'seal', 'keychain'];
  const playDuration = 32000;
  // The bar represents viewing time. The scene keeps its original chapter
  // coordinates so the existing card, engraving, and assembly paths stay exact.
  const timingKnots = [[0, 0], [.34, 3.45 / 8], [.66, 4.12 / 8], [1, 1]];
  const remapProgress = (value, from, to) => {
    const progress = clamp(value);
    for (let index = 1; index < timingKnots.length; index += 1) {
      const start = timingKnots[index - 1];
      const end = timingKnots[index];
      if (progress <= end[from]) {
        const fraction = (progress - start[from]) / (end[from] - start[from]);
        return start[to] + fraction * (end[to] - start[to]);
      }
    }
    return 1;
  };
  const toSceneProgress = (progress) => remapProgress(progress, 0, 1);
  const toDisplayProgress = (progress) => remapProgress(progress, 1, 0);
  const penStart = 3.60 / 8;
  // Still frames retain the separate tip, insertion, and secured-tip views
  // when the visitor has requested reduced motion.
  const reducedStops = [...stops.slice(0, 4), penStart, 3.77 / 8, 3.98 / 8, 4.08 / 8,
    ...stops.slice(5)].map(toDisplayProgress);

  class CsbProductTour extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      this._motion = matchMedia('(prefers-reduced-motion: reduce)');
      this._scene = this.querySelector('csb-product-scene');
      this._panel = this.querySelector('.csb-tour__panel');
      this._visual = this.querySelector('.csb-tour__visual');
      this._range = this.querySelector('[data-tour-range]');
      this._progressLabel = this.querySelector('[data-tour-progress]');
      this._play = this.querySelector('[data-tour-play]');
      this._sealDemo = this.querySelector('[data-tour-seal-demo]');
      this._sealProgress = 0;
      this._sealTween = null;
      this._status = this.querySelector('[data-tour-status]');
      this._leader = this.querySelector('svg[data-tour-leader]');
      this._callouts = [...this.querySelectorAll('[data-tour-callout]')];
      this._buttons = [...this.querySelectorAll('[data-tour-stop]')];
      this._progress = clamp(Number(this.dataset.progress) || 0);
      this._stage = -1;
      this._playing = false;
      this._tween = null;
      this._frame = 0;
      this._resizePending = true;
      this._renderPending = true;
      this._counters = new Map();
      this._counted = new WeakSet();
      this.dataset.enhanced = 'true';
      this.dataset.reducedMotion = String(this._motion.matches);

      const listen = (target, event, handler, options = {}) => {
        target?.addEventListener(event, handler, { ...options, signal: this._abort.signal });
      };
      listen(window, 'resize', () => this._resize(), { passive: true });
      listen(this, 'focusin', (event) => {
        if (event.target.matches('[data-tour-range], [data-tour-stop], [data-tour-play], [data-tour-seal-demo]')) {
          this._takeControl();
        }
      });
      listen(this._range, 'input', () => {
        this._takeControl();
        this._setProgress(Number(this._range.value) / 1000, 'manual');
        this._schedule();
      });
      listen(this._range, 'pointerdown', () => this._takeControl());
      listen(this, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        this._pause();
        this._tween = null;
        this._sealTween = null;
        if (document.activeElement === this._range) this._range.blur();
      });
      this._buttons.forEach((button) => {
        listen(button, 'click', () => this._goTo(Number(button.dataset.tourStop)));
      });
      listen(this._play, 'click', () => this._playing ? this._pause() : this._start());
      listen(this._sealDemo, 'click', () => {
        this._takeControl();
        const to = this._sealProgress > 0.5 ? 0 : 1;
        if (this._motion.matches) this._setSeal(to);
        else this._sealTween = { from: this._sealProgress, to, start: performance.now() };
        this._schedule();
      });
      listen(this._scene, 'csb:product-frame', (event) => {
        this._lastSceneFrame = event.detail;
        this._placeLeader(event.detail);
      });
      listen(document, 'visibilitychange', () => {
        if (document.hidden) {
          this._pause();
          this._tween = null;
          this._finishCounters();
        }
      });
      this._motionChange = () => {
        this.dataset.reducedMotion = String(this._motion.matches);
        const sealTarget = this._sealTween?.to;
        this._pause();
        this._tween = null;
        if (typeof sealTarget === 'number') this._setSeal(sealTarget);
        this._sealTween = null;
        if (this._motion.matches) this._finishCounters();
        this._renderPending = true;
        this._schedule();
      };
      this._motion.addEventListener('change', this._motionChange);
      if ('ResizeObserver' in window) {
        this._observer = new ResizeObserver(() => this._resize());
        this._observer.observe(this);
        if (this._panel) this._observer.observe(this._panel);
      }
      if ('IntersectionObserver' in window) {
        this._visibility = new IntersectionObserver((entries) => {
          if (!entries[0]?.isIntersecting) {
            this._pause();
            this._tween = null;
            this._finishCounters();
          }
        }, { threshold: 0 });
        this._visibility.observe(this);
      }
      this._setProgress(this._progress, 'initial');
      this._updatePlay();
      this._schedule();
      this._prepareScene();
      customElements.whenDefined('csb-product-scene').then(() => {
        if (!this._connected) return;
        this._renderPending = true;
        this._schedule();
      });
    }

    _prepareScene() {
      if (!this._scene || !this.dataset.sceneScript) return;
      const activate = () => {
        if (!this._connected || this._sceneLoading) return;
        this._sceneLoading = true;
        loadSceneScript(this.dataset.sceneScript).then(() => {
          this._sceneLoader?.disconnect();
          if (!this._connected) return;
          // A visitor may have moved the scrubber before the renderer arrived.
          this._scene.setProgress?.(this._sceneProgress);
          this._scene.setSealProgress?.(this._sealProgress);
        }).catch(() => {
          this._sceneLoading = false;
          if (this._connected) this._scene.dataset.ready = 'fallback';
        });
      };
      const options = { passive: true, signal: this._abort.signal };
      this.addEventListener('pointerdown', activate, options);
      this.addEventListener('focusin', activate, options);
      if ('IntersectionObserver' in window) {
        this._sceneLoader = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) activate();
        }, { rootMargin: '100px 0px' });
        this._sceneLoader.observe(this);
      } else activate();
    }

    disconnectedCallback() {
      this._connected = false;
      this._sceneLoader?.disconnect();
      this._sceneLoading = false;
      this._abort?.abort();
      this._observer?.disconnect();
      this._visibility?.disconnect();
      this._motion?.removeEventListener('change', this._motionChange);
      cancelAnimationFrame(this._frame);
      clearTimeout(this._playTimer);
      this._frame = 0;
      this._playing = false;
      this._counters?.clear();
    }

    _resize() {
      this._resizePending = true;
      this._schedule();
    }

    _schedule() {
      if (!this._frame && this._connected) {
        this._frame = requestAnimationFrame((now) => this._tick(now));
      }
    }

    _tick(now) {
      this._frame = 0;
      if (!this._connected) return;
      if (this._tween) {
        const tween = this._tween;
        const elapsed = clamp((now - tween.start) / tween.duration);
        this._setProgress(tween.from + (tween.to - tween.from) * ease(elapsed), 'manual');
        if (elapsed === 1) this._tween = null;
      }
      if (this._sealTween) {
        const tween = this._sealTween;
        const elapsed = clamp((now - tween.start) / 1200);
        this._setSeal(tween.from + (tween.to - tween.from) * ease(elapsed));
        if (elapsed === 1) this._sealTween = null;
      }
      if (this._playing && !this._motion.matches) {
        const progress = this._playFrom + (now - this._playStart) / playDuration;
        this._setProgress(clamp(progress), 'play');
        if (progress >= 1) this._pause();
      }
      if (this._renderPending || this._resizePending) {
        this._renderPending = false;
        this._resizePending = false;
        this._scene?.setProgress?.(this._sceneProgress);
        if (this._lastSceneFrame) this._placeLeader(this._lastSceneFrame);
      }
      this._counters.forEach((counter, element) => {
        const fraction = this._motion.matches ? 1 : clamp((now - counter.start) / 1150);
        const value = fraction === 1 ? counter.target : Math.floor(counter.target * ease(fraction));
        if (element.textContent !== String(value)) element.textContent = String(value);
        if (fraction === 1) this._counters.delete(element);
      });
      if (this._tween || this._sealTween || (this._playing && !this._motion.matches) || this._counters.size) this._schedule();
    }

    _setProgress(value, source) {
      const progress = clamp(Number.isFinite(value) ? value : 0);
      const sceneProgress = toSceneProgress(progress);
      const stage = Math.min(8, Math.floor(sceneProgress * 8 + 0.5));
      const changed = progress !== this._progress;
      this._progress = progress;
      this._sceneProgress = sceneProgress;
      this.dataset.progress = progress.toFixed(4);
      this.dataset.sceneProgress = sceneProgress.toFixed(4);
      this.dataset.stage = String(stage);
      if (this._progressLabel) this._progressLabel.textContent = `${Math.round(progress * 100)}%`;
      if (this._range) {
        const rangeValue = String(Math.round(progress * 1000));
        if (this._range.value !== rangeValue) this._range.value = rangeValue;
        this._range.style.setProperty('--tour-fill', `${progress * 100}%`);
      }
      if (this._stage !== stage) {
        this._stage = stage;
        if (stage !== 7) { this._sealTween = null; this._setSeal(0); }
        this._callouts.forEach((article) => {
          const active = Number(article.dataset.tourCallout) === stage;
          article.dataset.active = String(active);
          article.setAttribute('aria-hidden', String(!active));
          article.inert = !active;
          // Older theme markup may have included hidden. Opacity transitions
          // require every card to remain laid out in the shared callout grid.
          article.removeAttribute('hidden');
          if (active) this._count(article);
        });
        this._buttons.forEach((button) => {
          const active = Math.round(Number(button.dataset.tourStop) * 8) === stage;
          button.setAttribute('aria-pressed', String(active));
          if (active) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
          button.dataset.active = String(active);
          if (active) {
            const navigation = button.parentElement;
            const target = Math.max(0, Math.min(navigation.scrollWidth - navigation.clientWidth,
              button.offsetLeft - navigation.clientWidth / 2 + button.offsetWidth / 2));
            navigation.scrollTo({ left: target, behavior: this._motion.matches ? 'instant' : 'smooth' });
          }
        });
        const label = this._label(stage);
        this._range?.setAttribute('aria-valuetext', label);
        this.dispatchEvent(new CustomEvent('csb:tour-change', {
          bubbles: true, detail: { progress, sceneProgress, stage, source }
        }));
      }
      if ((source === 'manual' || source === 'play') && this._status) {
        const label = this._label(stage);
        if (this._status.textContent !== label) this._status.textContent = label;
      }
      if (changed || source === 'initial') this._renderPending = true;
      this._updatePlay();
    }

    _setSeal(value) {
      this._sealProgress = clamp(value);
      this._scene?.setSealProgress?.(this._sealProgress);
      this.dataset.sealProgress = this._sealProgress.toFixed(4);
      if (this._sealDemo) {
        const opened = this._sealProgress > .5;
        this._sealDemo.setAttribute('aria-pressed', String(opened));
        this._sealDemo.textContent = opened ? this._sealDemo.dataset.resetLabel : this._sealDemo.dataset.openLabel;
      }
    }

    _label(stage) {
      const article = this._callouts.find((item) => Number(item.dataset.tourCallout) === stage);
      return article?.dataset.label || article?.querySelector('h2, h3, h4')?.textContent?.trim() || '';
    }

    _count(article) {
      article.querySelectorAll('[data-tour-value]').forEach((element) => {
        if (this._counted.has(element)) return;
        const target = Number(element.dataset.tourValue || element.textContent.trim());
        if (!Number.isFinite(target) || target <= 0) return;
        this._counted.add(element);
        if (this._motion.matches) {
          element.textContent = String(target);
          return;
        }
        element.textContent = '0';
        this._counters.set(element, { target, start: performance.now() });
        this._schedule();
      });
    }

    _finishCounters() {
      this._counters.forEach((counter, element) => { element.textContent = String(counter.target); });
      this._counters.clear();
    }

    _takeControl() {
      this._pause();
      this._tween = null;
    }

    _goTo(value) {
      this._takeControl();
      // The Pen chapter opens before insertion, allowing Play to show the
      // complete preparation. Button identity still uses its original stop.
      const sceneTarget = value === stops[4] ? penStart : clamp(value);
      const target = toDisplayProgress(sceneTarget);
      if (this._motion.matches || Math.abs(target - this._progress) < 0.002) {
        this._setProgress(target, 'manual');
      } else {
        this._tween = { from: this._progress, to: target, start: performance.now(), duration: 650 };
      }
      this._schedule();
    }

    _start() {
      this._takeControl();
      if (this._progress >= 0.998) this._setProgress(0, 'play');
      this._playing = true;
      this._playFrom = this._progress;
      this._playStart = performance.now();
      this._updatePlay();
      // Reduced motion keeps every control available, with a sequence of
      // still viewpoints instead of continuous camera movement.
      if (this._motion.matches) this._queueReducedStep();
      this._schedule();
    }

    _queueReducedStep() {
      clearTimeout(this._playTimer);
      this._playTimer = setTimeout(() => {
        if (!this._connected || !this._playing || !this._motion.matches) return;
        const next = reducedStops.find((stop) => stop > this._progress + 0.001);
        if (next === undefined) {
          this._pause();
          return;
        }
        this._setProgress(next, 'play');
        this._schedule();
        if (next === 1) this._pause();
        else this._queueReducedStep();
      }, this._stage === 4 ? 3200 : 2800);
    }

    _pause() {
      this._playing = false;
      this._sealTween = null;
      clearTimeout(this._playTimer);
      this._updatePlay();
    }

    _updatePlay() {
      this.dataset.playing = String(Boolean(this._playing));
      if (!this._play) return;
      const label = this._playing ? this._play.dataset.pauseLabel :
        this._progress >= 0.998 ? this._play.dataset.replayLabel : this._play.dataset.playLabel;
      const text = this._play.querySelector('[data-tour-play-text]');
      if (text) {
        if (label && text.textContent !== label) text.textContent = label;
      } else if (label && this._play.textContent !== label) {
        this._play.textContent = label;
      }
      const icon = this._play.querySelector('[data-tour-play-icon], span[aria-hidden="true"]');
      if (icon) icon.textContent = this._playing ? 'Ⅱ' : this._progress >= 0.998 ? '↻' : '▷';
      if (label) this._play.setAttribute('aria-label', label);
      this._play.setAttribute('aria-pressed', String(Boolean(this._playing)));
    }

    _placeLeader(frame) {
      if (!this._leader || !this._visual || !this._scene || !frame) return;
      const active = this._callouts.find((article) => Number(article.dataset.tourCallout) === this._stage);
      const point = frame.anchors?.[keys[this._stage]] || frame.anchor;
      if (!point || !active || this._stage === 0 || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        this._leader.dataset.active = 'false';
        this._leader.style.opacity = '0';
        return;
      }
      const visualRect = this._visual.getBoundingClientRect();
      const sceneRect = this._scene.getBoundingClientRect();
      const cardRect = active.getBoundingClientRect();
      if (visualRect.width <= 0 || visualRect.height <= 0 || point.x < 0 || point.x > 1 || point.y < 0 || point.y > 1) {
        this._leader.dataset.active = 'false';
        this._leader.style.opacity = '0';
        return;
      }
      const x = sceneRect.left - visualRect.left + point.x * sceneRect.width;
      const y = sceneRect.top - visualRect.top + point.y * sceneRect.height;
      const centerX = cardRect.left - visualRect.left + cardRect.width / 2;
      const centerY = cardRect.top - visualRect.top + cardRect.height / 2;
      const horizontal = Math.abs(x - centerX) / Math.max(1, cardRect.width) >
        Math.abs(y - centerY) / Math.max(1, cardRect.height);
      const edgeX = horizontal ? (x < centerX ? cardRect.left : cardRect.right) - visualRect.left : centerX;
      const edgeY = horizontal ? centerY : (y < centerY ? cardRect.top : cardRect.bottom) - visualRect.top;
      const bendX = horizontal ? (edgeX + x) / 2 : edgeX;
      const bendY = horizontal ? edgeY : (edgeY + y) / 2;
      this._leader.setAttribute('viewBox', `0 0 ${visualRect.width} ${visualRect.height}`);
      this._leader.querySelector('polyline')?.setAttribute('points', `${edgeX},${edgeY} ${bendX},${bendY} ${x},${y}`);
      const dot = this._leader.querySelector('circle');
      dot?.setAttribute('cx', String(x));
      dot?.setAttribute('cy', String(y));
      this._leader.dataset.active = 'true';
      this._leader.style.opacity = this._scene.dataset.ready === 'true' ? '1' : '0';
    }
  }

  customElements.define('csb-product-tour', CsbProductTour);
})();
